<script setup lang="ts">
import { computed, ref, watch, inject, toRaw } from "vue";
import {
  useMagicKeys,
  onClickOutside,
  createReusableTemplate,
} from "@vueuse/core";
import useMobileBreakpoint from "../../composables/useMobileBreakpoint.ts";
import { Entity } from "./adapter";
import { FormKit } from "@formkit/vue";
import { whenAvailable } from "@formkit/utils";
import { VacvMobileOverlay } from "vacv-components";
import { useI18n } from "vue-i18n";

export type LocationFieldInputItems = {
  [groupName: string]: Entity[];
};
export interface Props {
  inputName?: string;
  label: string;
  placeholder?: string;
  tooltipMessage?: string;
  suggestions?: LocationFieldInputItems;
  loading?: boolean;
  noResult?: boolean;
  error?: boolean;
  defaultItem: Entity | null;
  isOrigin?: boolean;
  tabId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "select", item: Entity): void;
  (e: "type", input: string): void;
  (e: "setError", hasError?: boolean): void;
  (e: "onBlur"): void;
}>();

// eslint-disable-next-line @typescript-eslint/ban-types
const handleFirstClickForAnalytics = inject<Function>(
  "handleFirstClickForAnalytics"
);
// eslint-disable-next-line @typescript-eslint/ban-types
const setAnalyticsTimeout = inject<Function>("setAnalyticsTimeout");

const { t } = useI18n();
const desktopInputId = props.tabId + "-" + props.inputName + "ID";
const mobileInputId = props.tabId + "-" + props.inputName + "IDMobile";
const inputLabel = props.label;
const inputPlaceholder = props.placeholder ? props.placeholder : "";

const hasFocus = ref(false);
const modalOnDisplay = ref(false);
const elementHeight = ref("");
const containerRef = ref(null);
const highlighted = ref<Entity | null>(null);

const { isMobile } = useMobileBreakpoint();

const currentItem = ref<Entity | null>(null);
const currentSearch = ref("");
const selectedItem = computed(() => {
  return currentItem.value ? currentItem.value : props.defaultItem;
});

const suggestions = computed(() => {
  return props.suggestions;
});
const groupList = computed(() => {
  return props.suggestions ? Object.keys(props.suggestions) : null;
});

const hasResponse = computed(() => {
  return props.loading || props.noResult || props.suggestions || props.error;
});
const displayTooltip = computed(() => {
  return hasFocus.value && !hasResponse.value;
});

const infoMessage = computed(() => {
  return currentSearch.value.length && currentSearch.value.length < 3
    ? t("form.location.warningMessage")
    : props.tooltipMessage;
});

const flatList = computed(() => {
  let flat: Entity[] = [];
  if (suggestions.value) {
    const rawData = toRaw(suggestions.value);
    Object.keys(rawData).forEach((group) => {
      if (rawData[group]) {
        flat = flat.concat(rawData[group]);
      }
    });
  }
  return flat;
});
watch(flatList, (list) => {
  highlighted.value = list ? list[0] : null;
});

const { up, down, enter, tab } = useMagicKeys();
watch([up, down], ([up, down]) => {
  let selectedIndex = flatList.value.findIndex(
    (item) =>
      highlighted.value &&
      item.destinationId === highlighted.value.destinationId
  );
  if (up) {
    selectedIndex =
      selectedIndex === 0 ? flatList.value.length - 1 : selectedIndex - 1;
  } else if (down) {
    selectedIndex =
      selectedIndex + 1 === flatList.value.length ? 0 : selectedIndex + 1;
  }
  highlighted.value = flatList.value[selectedIndex];
});
watch([enter, tab], ([a, b]) => {
  if (a || b) {
    if (highlighted.value) {
      selectItem(highlighted.value);
    }
  }
});

const onHoverItem = (hoveredItem: Entity) => {
  const selectedIndex = flatList.value.findIndex(
    (item) => hoveredItem && item.destinationId === hoveredItem.destinationId
  );
  highlighted.value = flatList.value[selectedIndex];
};

const onInput = (value?: string) => {
  if (typeof value === "string") {
    currentSearch.value = value;
    emit("type", value);
  }
};

const onFocus = (event: KeyboardEvent) => {
  hasFocus.value = true;
  const currentTarget = event.currentTarget as HTMLInputElement;
  currentTarget.select();
  if (handleFirstClickForAnalytics) {
    handleFirstClickForAnalytics();
  }
  if (setAnalyticsTimeout) {
    setAnalyticsTimeout();
  }
};

const blurInput = () => {
  // let time for selecting the element in case the user has clicked on an item
  if (!modalOnDisplay.value) {
    setTimeout(leaveInput, 300);
  }
};

const leaveInput = () => {
  hasFocus.value = false;
  if (!isMobile.value) {
    emit("onBlur");
  }
};

// set the height of the component so that the tooltip appears
// above the formkit error
whenAvailable(desktopInputId, (element) => {
  if (element.clientHeight) {
    elementHeight.value = element.clientHeight + "px";
  }
});
onClickOutside(containerRef, leaveInput);

watch(hasFocus, (activeState) => {
  if (activeState) {
    if (isMobile.value) {
      modalOnDisplay.value = true;
    }
  } else {
    modalOnDisplay.value = false;
  }
});

const selectItem = (item: Entity) => {
  currentItem.value = item;
  if (isMobile.value) {
    modalOnDisplay.value = false;
  } else {
    emit("select", currentItem.value);
    emit("onBlur");
  }
};

// wait until the modal enter Transition animation is completed before
// adding focus to the mobile input and select the content
const onModalEnter = () => {
  document.getElementById(mobileInputId)!.focus();
  (document.getElementById(mobileInputId) as HTMLInputElement).select();
};
// wait until the modal Transition animation is completed before
// emit the event to the parent which results into the rerender of the component
const onModalClose = () => {
  (document.getElementById(desktopInputId) as HTMLInputElement).blur();
  if (currentItem.value) {
    emit("select", currentItem.value);
  }
  emit("onBlur");
};

type TemplateVersion = "modal" | "in-form";
// This is to avoid to display the overlay (.result-overlay) twice used within `ReuseTemplate`,
// Without it, in mobile version, when mobile modal is closed and during the animation down
// we can see the overlay used by desktop displayed underneath until the mobile modal totally disappears.
const shouldDisplay = (templateVersion: TemplateVersion) => {
  return (
    (isMobile.value && templateVersion === "modal") ||
    (!isMobile.value && templateVersion === "in-form")
  );
};

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();
</script>

<template>
  <DefineTemplate v-slot="{ inputId, templateVersion }">
    <div
      class="LocationField"
      :class="{
        departure: isOrigin,
        arrival: !isOrigin,
        mobile: templateVersion === 'modal',
      }"
      ref="containerRef"
    >
      <FormKit
        type="text"
        :name="props.inputName + 'FK'"
        class="LocationField-input"
        :id="inputId"
        tabindex="0"
        ref="oneInput"
        :label="inputLabel"
        label-class="sr-only"
        :placeholder="inputPlaceholder"
        validation="required|length:3"
        wrapper-class="faplane"
        data-testid="LocationField-field"
        :value="selectedItem?.name"
        autocomplete="off"
        @focus="onFocus($event)"
        @blur="blurInput()"
        @input="onInput($event)"
        @keydown.enter.prevent
        :validation-messages="{
          required: $t('form.location.error.required'),
          length: $t('form.location.error.length', {
            min: 3,
          }),
        }"
      />
      <input
        type="hidden"
        :name="inputName"
        :value="selectedItem?.destinationId"
      />
      <div
        class="result-overlay"
        v-if="shouldDisplay(templateVersion) && (hasResponse || displayTooltip)"
      >
        <div v-if="displayTooltip" class="result-overlay__tooltip">
          {{ infoMessage }}
        </div>
        <div class="result" v-else-if="suggestions">
          <ul class="result-list nolist">
            <li
              class="result-group__item"
              v-for="groupName in groupList"
              :key="groupName"
            >
              <div class="result-group__title">
                {{ $t(`form.location.${groupName}`) }}
              </div>
              <ul class="result-list nolist">
                <li
                  v-for="item in suggestions[groupName]"
                  :key="item.destinationId"
                  @click="selectItem(item)"
                  @keyup.enter="selectItem(item)"
                  @mouseenter="onHoverItem(item)"
                  role="button"
                  class="result-list__item"
                  :class="{
                    'result-list__item--selected':
                      highlighted?.destinationId === item.destinationId,
                  }"
                >
                  {{ item.name }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="no-result" v-else-if="noResult || error">
          {{ $t("form.location.error.no-results") }}
        </div>
        <div class="result-loading" v-else-if="loading">...</div>
      </div>
    </div>
  </DefineTemplate>
  <VacvMobileOverlay
    :on-display="isMobile && modalOnDisplay"
    @close="modalOnDisplay = false"
    @after-enter="onModalEnter"
    @after-leave="onModalClose"
  >
    <ReuseTemplate :input-id="mobileInputId" template-version="modal" />
  </VacvMobileOverlay>
  <ReuseTemplate :input-id="desktopInputId" template-version="in-form" />
</template>

<style lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.vacv-form-item.flight {
  + .flight {
    .formkit-wrapper.faplane {
      .formkit-inner::before {
        content: "\f5af";
      }
    }
  }
}

.formkit-wrapper.faplane {
  .formkit-inner::before {
    display: block;
    position: absolute;
    width: 20px;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    content: "\f5b0";
    font: var(--fa-font-solid);
    color: $col-black;
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
  }
}

.LocationField {
  position: relative;

  &.mobile {
    margin-top: 40px;
  }

  .delete-button {
    border: 0;
    position: absolute;
    z-index: 1;
    display: flex;
    justify-content: center;
    right: 5px;
    top: 10px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    cursor: pointer;
  }

  .formkit-input[type="text"] {
    padding: 10px 0 10px 30px;
    text-overflow: ellipsis;

    &:focus {
      &::placeholder {
        position: absolute;
        top: 5px;
        font-size: 1.2rem;
        transition: all 0.2s ease;
      }
    }
  }

  .result-overlay {
    z-index: 1000;
    position: absolute;
    top: v-bind("elementHeight");
    left: 0;
    display: block;
    min-width: 100%;
    max-height: 300px;
    margin-top: 5px;
    font-size: 1.4rem;
    color: $col-black;
    background-color: $col-white;
    border-radius: $border-radius;
    border: $border-greylt;
    box-shadow: $box-shadow-bottom;
    overflow-y: auto;
    scrollbar-width: thin;

    .result-loading,
    .no-result {
      padding: 10px;
      color: $col-greymd;
    }

    &__tooltip {
      width: 100%;
      padding: 10px;
      background-color: white;

      @media #{$desktop-sm} {
        white-space: nowrap;
      }
    }

    .result-error {
      padding: 10px;
      color: $error-text;

      .formkit-messages {
        padding: 0 10px;

        .formkit-message {
          margin-top: 0;
        }
      }
    }

    .result {
      // common to all result-list including nested
      ul.result-list {
        @include reset-list;

        li {
          background-color: $col-white;

          @media #{$desktop-sm} {
            white-space: nowrap;
          }

          &.result-list__item {
            color: $col-black;
            cursor: pointer;

            &--selected {
              background-color: $col-greypale;
            }
          }
        }
      }

      // first result list (not a nested child list)
      > ul.result-list {
        > li.result-group__item {
          .result-group__title {
            padding: 10px;
            background-color: $col-greylt;
            @include font-med19;
          }

          // nested child list
          ul.result-list li {
            padding: 10px;
          }
        }
      }
    }
  }
}
</style>
